import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * PriceApiService (Angular 18) — person types and price lists.
 *
 * Replaces apps/b2b-ui/common/services/api/priceApi/priceApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.price.types.{search,list,create,update,remove}    person types
 *   apiService.price.lists.{list,create,update,detail,remove}    price lists
 *
 * Legacy quirks preserved:
 *   - `types.search()` always appends `&search=` even when omitted by the
 *     caller (matches the `proxyUserApi.search()` pattern).
 *   - `lists.list()` and `types.list()` translate `props.inactive: true` into
 *     `includeInactive=true` on the querystring.
 */
const PRICE_BASE_V1 = '/v1/api/price';
const PRICE_BASE_V2 = '/v2/api/price';

const TYPES_SEARCH_FIELDS = ['search', 'page', 'pageSize', 'sortDirection', 'sortField', 'totalItems'] as const;

const TYPES_LIST_FIELDS = ['types', 'includeInactive', ...PAGINATION_SEARCH_FIELDS] as const;

function withIncludeInactive(props?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!props) return props;
  if (props['inactive']) {
    return { ...props, includeInactive: true };
  }
  return props;
}

interface PersonTypePayload {
  id?: number | string;
  name: string;
  description?: string;
  active?: boolean;
  type?: string;
  defaultPrice?: unknown;
  displayOrder?: number;
  children?: unknown;
}

interface PriceListPayload {
  id?: number | string;
  name: string;
  description?: string;
  effectiveStartTime?: unknown;
  effectiveEndTime?: unknown;
  priceListType?: string;
  prices?: unknown[];
}

@Injectable({ providedIn: 'root' })
export class PriceApiService {
  private readonly http = inject(HttpClient);

  readonly types = {
    search: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> => {
      let params = buildParams(props, TYPES_SEARCH_FIELDS);
      if (!params.has('search')) {
        params = params.set('search', '');
      }
      return wrapAjs(this.http.get<unknown>(`${PRICE_BASE_V1}/person/types/names`, { params }));
    },

    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${PRICE_BASE_V2}/person/types`, {
          params: buildParams(withIncludeInactive(props), TYPES_LIST_FIELDS),
        }),
      ),

    create: (props: PersonTypePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${PRICE_BASE_V1}/person/types`, {
          name: props.name,
          description: props.description,
          active: props.active,
          type: props.type,
          defaultPrice: props.defaultPrice,
          displayOrder: props.displayOrder,
          children: props.children,
        }),
      ),

    update: (props: PersonTypePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${PRICE_BASE_V1}/person/types`, {
          id: props.id,
          name: props.name,
          description: props.description,
          active: props.active,
          type: props.type,
          defaultPrice: props.defaultPrice,
          displayOrder: props.displayOrder,
          children: props.children,
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${PRICE_BASE_V1}/person/types/${id}`)),
  };

  readonly lists = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${PRICE_BASE_V2}/lists`, {
          params: buildParams(withIncludeInactive(props), PAGINATION_SEARCH_FIELDS),
        }),
      ),

    create: (props: PriceListPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${PRICE_BASE_V1}/lists`, {
          name: props.name,
          description: props.description,
          effectiveStartTime: props.effectiveStartTime,
          effectiveEndTime: props.effectiveEndTime,
          priceListType: props.priceListType,
          prices: props.prices,
        }),
      ),

    // Note: legacy `update()` omits `effectiveEndTime` from the body —
    // preserved 1:1 (likely a backend contract decision; the endpoint
    // computes the end time server-side).
    update: (props: PriceListPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${PRICE_BASE_V1}/lists`, {
          id: props.id,
          name: props.name,
          description: props.description,
          effectiveStartTime: props.effectiveStartTime,
          priceListType: props.priceListType,
          prices: props.prices,
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${PRICE_BASE_V1}/lists/${id}`)),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${PRICE_BASE_V1}/lists/${id}`)),
  };
}
